import { gql } from '@apollo/client';
import { useAlert } from 'components/alert/AlertStack';
import { Button } from 'components/Button';
import { useDomain } from 'components/domain/DomainProvider';
import { CustomFormField } from 'components/field/form/CustomFormField';
import { Form } from 'components/form/Form';
import { FormGroup } from 'components/form/FormGroup';
import { Spinner } from 'components/Spinner';
import { Text } from 'components/Text';
import { React, useQuery } from 'libs/react';
import { StoreStepContentHeader } from './components/StoreStepContentHeader';
import { fieldsByOrder, findMissingRequired, parseUnitFieldValues, readLineAnswers, toUnitsInput, unitFieldKey } from './storeVoucherFields';
import './StoreVoucherFieldsSection.scss';
/**
 * Lines of the cart with the number of vouchers each one creates
 * @const StoreDealVoucherFieldsQuery
 * @since 1.0.0
 */
const StoreDealVoucherFieldsQuery = gql `
  query StoreDealVoucherFields($input: DealInput!) {
    deal(input: $input) {
      id
      items {
        id
        quantity
        proposed
        voucherCount
        unitFieldValues
        item {
          id
        }
      }
    }
  }
`;
/**
 * The fields a product asks for each voucher (participant name, allergies…),
 * filled once per voucher before the order step.
 *
 * @component StoreVoucherFieldsSection
 * @since 1.0.0
 */
export function StoreVoucherFieldsSection(props) {
    var _a, _b, _c;
    const { dealId, saleItems, processing, onBack, onNext } = props;
    const alert = useAlert();
    const { options } = useDomain();
    const backgroundImage = (_a = options.backgroundImage) !== null && _a !== void 0 ? _a : null;
    const [data, ready] = useQuery(StoreDealVoucherFieldsQuery, {
        variables: { input: { id: dealId } },
        fetchPolicy: 'network-only'
    });
    const lines = ((_c = (_b = data === null || data === void 0 ? void 0 : data.deal) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : [])
        .filter((line) => !line.proposed && line.voucherCount > 0)
        .map((line) => {
        var _a;
        const saleItem = saleItems.find((entry) => entry.id === line.item.id);
        const groups = ((_a = saleItem === null || saleItem === void 0 ? void 0 : saleItem.voucherFieldGroups) !== null && _a !== void 0 ? _a : []);
        return { line, saleItem, groups };
    })
        .filter(({ groups }) => groups.length > 0);
    /**
     * @function onSubmit
     * @since 1.0.0
     * @hidden
     */
    function onSubmit(values) {
        const unitsByLine = new Map();
        let missing = 0;
        for (const { line, groups } of lines) {
            const answers = readLineAnswers(values.fields, line, groups);
            missing += findMissingRequired(answers, groups).length;
            unitsByLine.set(line.id, toUnitsInput(answers));
        }
        if (missing > 0) {
            alert.present({
                title: React.createElement("p", null, t('Please fill in the required fields (*).'))
            });
            return;
        }
        onNext(unitsByLine);
    }
    return (React.createElement("div", { className: "section store-voucher-fields-section", style: {
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none'
        } },
        React.createElement(StoreStepContentHeader, { backDisabled: processing, backgroundImage: backgroundImage, onBack: onBack, title: t('A few details for your order') }),
        !ready && !data ? (React.createElement(Spinner, null)) : (React.createElement(Form, { name: "store-voucher-fields-form", className: "store-voucher-fields-section__form", onSubmit: onSubmit },
            React.createElement("div", { className: "store-voucher-fields-section__content" }, lines.map(({ line, saleItem, groups }) => {
                const answers = parseUnitFieldValues(line.unitFieldValues);
                return Array.from({ length: line.voucherCount }, (_, unit) => {
                    var _a;
                    return (React.createElement("div", { key: `${line.id}-${unit}`, className: "store-voucher-fields-section__unit" },
                        React.createElement("div", { className: "store-voucher-fields-section__unit-header" },
                            React.createElement(Text, { className: "store-voucher-fields-section__unit-title", value: (_a = saleItem === null || saleItem === void 0 ? void 0 : saleItem.title) !== null && _a !== void 0 ? _a : '' }),
                            React.createElement(Text, { className: "store-voucher-fields-section__unit-count", value: `${unit + 1} / ${line.voucherCount}` +
                                    (unit >= line.quantity ? ` · ${t('Offered')}` : '') })),
                        React.createElement(FormGroup, null, fieldsByOrder(groups).map((field) => {
                            var _a;
                            return (React.createElement(CustomFormField, { key: field.id, field: withAnswer(field, (_a = answers[unit]) === null || _a === void 0 ? void 0 : _a[field.id]), fieldId: unitFieldKey(line.id, unit, field.id), noDefaultValue: true, translable: false, fromPublic: true }));
                        }))));
                });
            })),
            React.createElement("div", { className: "store-voucher-fields-section__footer" },
                React.createElement(Button, { className: "store-voucher-fields-section__next", label: t('Continue'), processing: processing, disabled: processing, submit: true }))))));
}
/**
 * The field with its saved answer (else its default value, prefilled as a
 * plain value: the customer does not need the "set by default" notice), and
 * a mark on the label when required
 * @function withAnswer
 * @since 1.0.0
 * @hidden
 */
function withAnswer(field, answer) {
    const value = answer != null
        ? JSON.stringify({ value: answer })
        : field.defaultValue != null && field.defaultValue !== ''
            ? field.defaultValue
            : null;
    return Object.assign(Object.assign({}, field), { value, translations: field.required
            ? field.translations.map((translation) => (Object.assign(Object.assign({}, translation), { title: `${translation.title} *` })))
            : field.translations });
}
